import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Grid, Chip, CircularProgress, Button } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Layout from '../components/Layout';
import { fetchAllPosts } from '../utils/blogLoader';

const ALL_CATEGORIES = ['All', 'Digital Transformation', 'Operations', 'GearDesk', 'LineDesk', 'IT Consulting', 'Software Licensing', 'DevOps', 'Manufacturing', 'General'];
const POSTS_PER_PAGE = 6;

const BlogsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [page, setPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    // SEO meta tags
    document.title = 'Blog — Insights & Guides | InfyniHub';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Practical advice on digital transformation, IT operations, and software for small and medium businesses.');
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', 'Blog — Insights & Guides | InfyniHub');
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', 'Practical advice on digital transformation, IT operations, and software for small and medium businesses.');
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', 'https://infynihub.com/blogs');
    fetchAllPosts().then((data) => { setPosts(data); setLoading(false); });
  }, []);

  // Reset to page 1 when category changes
  useEffect(() => { setPage(1); }, [activeCategory]);

  const filtered = activeCategory === 'All' ? posts : posts.filter((p) => p.category === activeCategory);
  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);

  // Only show categories that have posts + All + General always visible
  const visibleCategories = ALL_CATEGORIES.filter(
    (cat) => cat === 'All' || cat === 'General' || posts.some((p) => p.category === cat)
  );

  return (
    <Layout>
      {/* JSON-LD for blog listing */}
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'InfyniHub Blog',
        url: 'https://infynihub.com/blogs',
        description: 'Practical advice on digital transformation, IT operations, and software for SMEs.',
        publisher: { '@type': 'Organization', name: 'InfyniHub', url: 'https://infynihub.com' },
      })}</script>

      <Box sx={{ bgcolor: '#FFFFFF', pt: { xs: '140px', md: '150px' } }}>

        {/* Header */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#F8F9FF', borderBottom: '1px solid #F3F4F6' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '100px', px: 2, py: 0.6, mb: 3 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366F1' }} />
              <Typography sx={{ color: '#4F46E5', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Blogs</Typography>
            </Box>
            <Typography component="h1" sx={{ fontWeight: 900, color: '#0F1117', fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', mb: 2 }}>
              Insights & Guides
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '1.05rem', lineHeight: 1.8, maxWidth: 560 }}>
              Practical advice on digital transformation, IT operations, and software for small and medium businesses.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 4 }}>
              {visibleCategories.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  onClick={() => setActiveCategory(cat)}
                  sx={{
                    fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                    bgcolor: activeCategory === cat ? '#6366F1' : '#FFFFFF',
                    color: activeCategory === cat ? '#FFFFFF' : '#6B7280',
                    border: activeCategory === cat ? '1px solid #6366F1' : '1px solid #E5E7EB',
                    '&:hover': { bgcolor: activeCategory === cat ? '#5254CC' : '#F3F4F6' },
                  }}
                />
              ))}
            </Box>
          </Container>
        </Box>

        {/* Posts grid */}
        <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#FFFFFF' }}>
          <Container maxWidth="lg" sx={{ px: { xs: 3, md: 4 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#6366F1' }} />
              </Box>
            ) : paginated.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography sx={{ color: '#6B7280', fontSize: '1rem' }}>No posts in this category yet.</Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={3}>
                  {paginated.map((post) => (
                    <Grid item xs={12} sm={6} md={4} key={post.slug}>
                      <Box
                        onClick={() => navigate(`/blogs/${post.slug}`)}
                        sx={{
                          height: '100%', display: 'flex', flexDirection: 'column',
                          bgcolor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '16px',
                          overflow: 'hidden', cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          '&:hover': { borderColor: '#6366F1', transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(99,102,241,0.1)' },
                        }}
                      >
                        {post.coverImage && (
                          <Box component="img" src={post.coverImage} alt={post.title}
                            sx={{ width: '100%', height: 180, objectFit: 'cover' }} />
                        )}
                        {!post.coverImage && <Box sx={{ height: 4, bgcolor: '#6366F1' }} />}
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                          <Box sx={{ display: 'inline-block', bgcolor: '#EEF2FF', borderRadius: '100px', px: 1.5, py: 0.4, mb: 2, alignSelf: 'flex-start' }}>
                            <Typography sx={{ color: '#4F46E5', fontSize: '0.7rem', fontWeight: 700 }}>{post.category}</Typography>
                          </Box>
                          <Typography sx={{ fontWeight: 800, color: '#0F1117', fontSize: '1rem', lineHeight: 1.4, mb: 1.5, letterSpacing: '-0.01em' }}>
                            {post.title}
                          </Typography>
                          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', lineHeight: 1.7, mb: 3, flexGrow: 1 }}>
                            {post.excerpt}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2, borderTop: '1px solid #F3F4F6' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                              <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{post.readTime}</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#D1D5DB', mx: 0.5 }}>·</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>{post.date}</Typography>
                            </Box>
                            <ArrowForwardIcon sx={{ fontSize: 16, color: '#6366F1' }} />
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 6 }}>
                    <Button
                      disabled={page === 1}
                      onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      sx={{ minWidth: 40, height: 40, borderRadius: '8px', border: '1px solid #E5E7EB', color: '#6B7280', fontWeight: 600, '&:hover': { borderColor: '#6366F1', color: '#6366F1' }, '&:disabled': { opacity: 0.35 } }}
                    >←</Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <Button
                        key={p}
                        onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        sx={{
                          minWidth: 40, height: 40, borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem',
                          bgcolor: page === p ? '#6366F1' : 'transparent',
                          color: page === p ? '#FFFFFF' : '#6B7280',
                          border: page === p ? '1px solid #6366F1' : '1px solid #E5E7EB',
                          '&:hover': { bgcolor: page === p ? '#5254CC' : '#F3F4F6', borderColor: '#6366F1' },
                        }}
                      >{p}</Button>
                    ))}
                    <Button
                      disabled={page === totalPages}
                      onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      sx={{ minWidth: 40, height: 40, borderRadius: '8px', border: '1px solid #E5E7EB', color: '#6B7280', fontWeight: 600, '&:hover': { borderColor: '#6366F1', color: '#6366F1' }, '&:disabled': { opacity: 0.35 } }}
                    >→</Button>
                  </Box>
                )}
              </>
            )}
          </Container>
        </Box>
      </Box>
    </Layout>
  );
};

export default BlogsPage;
